import { Drawer } from "@base-ui/react/drawer";
import type { ReactElement, ReactNode } from "react";
import { cx, usePortalContainer, usePresentation } from "../presentation.tsx";
import { IconButton } from "./Button.tsx";

export type SheetSide = "bottom" | "right";

export interface SheetProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  trigger?: ReactElement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Default: a bottom sheet on mobile and kiosk, a right-hand panel on console. */
  side?: SheetSide;
  className?: string;
}

/** A swipe-dismissable panel (Base UI Drawer). */
export function Sheet({ title, description, children, actions, trigger, open, defaultOpen, onOpenChange, side, className }: SheetProps) {
  const presentation = usePresentation();
  const container = usePortalContainer();
  const resolved: SheetSide = side ?? (presentation === "console" ? "right" : "bottom");
  return (
    <Drawer.Root
      open={open} defaultOpen={defaultOpen} swipeDirection={resolved === "bottom" ? "down" : "right"}
      onOpenChange={onOpenChange ? (o) => onOpenChange(o) : undefined}
    >
      {trigger && <Drawer.Trigger render={trigger} />}
      <Drawer.Portal container={container}>
        <Drawer.Backdrop className="pmu-backdrop" />
        <Drawer.Viewport className={cx("pmu-sheet__viewport", `pmu-sheet__viewport--${resolved}`)}>
          <Drawer.Popup className={cx("pmu-popup pmu-sheet", `pmu-sheet--${resolved}`, className)}>
            {resolved === "bottom" && <div className="pmu-sheet__handle" aria-hidden="true" />}
            <Drawer.Content className="pmu-sheet__content">
              <div className="pmu-dialog__header">
                <Drawer.Title className="pmu-dialog__title">{title}</Drawer.Title>
                <Drawer.Close render={<IconButton icon="close" label="Close" />} />
              </div>
              {description && <Drawer.Description className="pmu-dialog__description">{description}</Drawer.Description>}
              {children && <div className="pmu-dialog__body">{children}</div>}
              {actions && <div className="pmu-dialog__actions">{actions}</div>}
            </Drawer.Content>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** Makes its child element close the enclosing Sheet. */
export function SheetClose({ children }: { children: ReactElement }) {
  return <Drawer.Close render={children} />;
}
